import { Dialog, Spinner, Tag } from "construct-ui";
import QueryListComponent from "../query-list/query-list.component";
import languageModel from "../../models/language.model";

/**
 * Simple Search Dialog
 * 
 * Reusable component that will show a dialog with search functionality.
 * Supports both single and multiple inputs.
 * 
 * Uses QueryListComponent that supports infinite scrolling.
 * 
 * @author Darrus Goh 
 */
export default function SimpleSearchDialog() {
    const DEFAULT_SEARCH_TEXT = '';
    const DEFAULT_PAGE_INDEX = 1;
    const DEFAULT_ONSEARCH = () => { console.log('SimpleSearchDialog: onSearch(text, pageIndex, pageSize)') };
    const DEFAULT_ONSELECT = () => { console.log('SimpleSearchDialog: onSelect(selectedItems | selectedItem)'); };
    const DEFAULT_ONINFINITESCROLL = () => { console.log('SimpleSearchDialog: onInfiniteScroll(searchText, pageIndex, pageSize)'); };
    const DEFAULT_ONCLOSE = () => { console.log('SimpleSearchDialog: onClose()'); };
    const DEFAULT_ONCLEAR = () => { console.log('SimpleSearchDialog: onClear()') };

    let searchText = DEFAULT_SEARCH_TEXT;
    let pageIndex = DEFAULT_PAGE_INDEX;

    const onRemove = (selectedItems, item, onSelect, key) => () => {
        const result = structuredClone(selectedItems);
        const itemIndex = result.findIndex((e) => e[key] === item[key]);
        result.splice(itemIndex, 1);
        onSelect(result);
    };

    const onDeselectAll = (onSelect) => () => {
        onSelect([]);
    }

    const handleOnClear = (onClear) => () => {
        searchText = DEFAULT_SEARCH_TEXT;
        pageIndex = DEFAULT_PAGE_INDEX;
        onClear();
    }

    return {
        view: ({ attrs: {
            items = [],
            selectedItems = [],
            selectedItem = null,
            totalItemsCount = 0,
            pageSize = 100,
            isOpen = false,
            multiple = false,
            itemKey = 'code',
            itemValue = 'name',
            title = 'Simple Search Dialog',
            searchPlaceholderText = 'Search',
            isSearching = false,

            onSelect = DEFAULT_ONSELECT,
            onSearch = DEFAULT_ONSEARCH,
            onInfiniteScroll = DEFAULT_ONINFINITESCROLL,
            onClose = DEFAULT_ONCLOSE,
            onClear = DEFAULT_ONCLEAR
        } }) => m(Dialog,
            {
                title,
                class: 'cui-dialog-l',
                closeOnOutsideClick: false,
                isOpen,
                onClose,
                content: m('.vstack.gap-3', { style: { height: '70vh' } },
                    multiple && m('div', { style: { maxHeight: '25%' } },
                        m('.vstack.h-100',
                            m('.hstack.justify-content-between.mb-1',
                                m('span', `${languageModel.getText("LT00000346", "Selected items")}: ${selectedItems.length}`),
                                m('a.btn.btn-link.p-0', { onclick: onDeselectAll(onSelect) }, languageModel.getText("LT00000345", "Clear all"))
                            ),
                            m('div', { style: { overflowY: 'hidden' } },
                                m('.card.h-100',
                                    m('.card-body.h-100', { style: { overflowY: 'auto' } },
                                        selectedItems.length === 0
                                            ? m('.text-center', m('small.text-muted', languageModel.getText("LT00000318", "No items selected. Please search and select from the section below.")))
                                            : selectedItems.map(item => m(Tag, {
                                                label: item.name,
                                                onRemove: onRemove(selectedItems, item, onSelect, itemKey)
                                            }))
                                    )
                                )
                            )
                        )
                    ),
                    m('.flex-fill.vstack', { style: { overflowY: 'hidden' } },
                        m('.flex-fill', { style: { overflowY: 'hidden' } },
                            m('.card.h-100',
                                m('.card-body.h-100.vstack',
                                    m('form.hstack.gap-2.mb-2', {
                                        onsubmit: (e) => {
                                            e.preventDefault();
                                            pageIndex = DEFAULT_PAGE_INDEX;
                                            onSearch(searchText, pageIndex, pageSize);
                                        }
                                    },
                                        m('input.flex-fill.form-control.form-control-bg', {
                                            type: 'text',
                                            value: searchText,
                                            placeholder: searchPlaceholderText,
                                            onchange: (e) => { searchText = e.target.value; }
                                        }),
                                        m('button.btn.btn-primary', { onclick: () => { } }, languageModel.getText('LT00000001', 'Search')),
                                        m('button.btn.btn-white', { type: 'button', onclick: handleOnClear(onClear) }, languageModel.getText('LT00000031', 'Clear'))

                                    ),
                                    m('div.flex-fill', { style: { overflowY: 'hidden' } },
                                        m(QueryListComponent, {
                                            filterable: false,
                                            multiple,
                                            items,
                                            itemValue,
                                            itemKey,
                                            selectedItems,
                                            selectedItem,
                                            onSelect,
                                            onInfiniteScroll: () => {
                                                onInfiniteScroll(searchText, ++pageIndex, pageSize);
                                            }
                                        }),
                                        m(Spinner, { fill: true, background: true, active: isSearching })
                                    )
                                )
                            )
                        ),
                        m('div.text-end',
                            m('small', `${items?.length} / ${totalItemsCount} ${languageModel.getText('LT00000364', 'record(s)')}`)
                        )
                    )
                )
            }
        )
    }
}